import React, { useState, useEffect } from "react";
import { getFirestore, doc, setDoc, onSnapshot, deleteDoc, getDoc, collection, runTransaction } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaEdit, FaTrash } from "react-icons/fa";

// Import Atoms
import SplashScreen from "../../components/UI/atoms/splash/SplashScreen"
import TextFieldComp from "../../components/UI/atoms/text-fields/TextField";

// Import Organisms
import Header from "../../components/UI/organisms/header/logged-in/HeaderLoggedIn"

// Imports MUI Components
import {  Box,
          Button,
          Stack,
          Typography,
          Grid,
          CircularProgress,
          TableContainer,
          Table,
          TableHead,
          TableRow,
          TableCell,
          Paper,
          TableBody, 
          TableFooter,
          Pagination,
          Dialog,
          DialogTitle,
          DialogContent,
          DialogActions,
          IconButton,
          Tooltip,
          Snackbar,
          Alert as MuiAlert,
          Divider
} from "@mui/material"

// Import Organisms
import Sidebar from "../../components/UI/organisms/sidebar/Sidebar"

// Import Functions
import { fetchUserDetails } from "../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../components/functions/notifyFirstUser";
import { redirectIfNotLoggedIn } from "../../components/functions/redirectIfNotLoggedIn";
import Icon from "../../components/UI/atoms/icon/Icon";
import Fab from "../../components/UI/atoms/fab/Fab";
import { Close } from "@mui/icons-material";
import Text from "../../components/UI/atoms/text/Text";


const ManageInventory = () => {
  // state variables
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [itemName, setItemName] = useState("");
  const [itemNo, setItemNo] = useState(null);
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [quantity, setQuantity] = useState("");
  const [inventoryList, setInventoryList] = useState([]);
  const [sortField, setSortField] = useState("itemNo");
  const [sortOrder, setSortOrder] = useState("asc");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  
  // Touch-based validation states
  const [touchedFields, setTouchedFields] = useState({
    itemName: false,
    pricePerUnit: false,
    quantity: false,
  });
  const [fieldErrors, setFieldErrors] = useState({
    itemName: "",
    pricePerUnit: "",
    quantity: "",
  });

  useEffect(() => {
   setErrorOpen(Boolean(errorMessage));
 }, [errorMessage]);

  const [userDetails, setUserDetails] = useState(null);
  const auth = getAuth();
  const [, setOpenMenuKey] = useState(null);
  const [showAddUpdateSnackbar, setShowAddUpdateSnackbar] = useState(false);
  const [addUpdateSnackbarMessage, setAddUpdateSnackbarMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [deleteSnackbarMessage, setDeleteSnackbarMessage] = useState("");
  const [isDeletingInventoryItem, setIsDeletingInventoryItem] = useState(false);
  const [isCancellingInventoryDelete, setIsCancellingInventoryDelete] = useState(false);

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertImage, ] = useState("");
  const [alertButton, ] = useState("")
  const [alertButtonType, ] = useState("false")
  const [alertLink, ] = useState("")

  // Calculate total cost based on price per unit and quantity
  const totalCost = (parseFloat(pricePerUnit) || 0) * (parseInt(quantity) || 0);
  const db = getFirestore();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Store unsubscribe functions for manual cleanup
  const [, setUnsubscribeFunctions] = useState([]);

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert);
    const notify = notifyFirstUser();
    return () => {
      details && details();
      redirect && redirect();
      notify && notify();
    };
  }, []);

  // Function to check if item name already exists (case-insensitive)
  const isItemNameExists = (name, excludeKey = null) => {
    if (!name) return false;
    const target = name.toLowerCase().trim();
    return inventoryList.some(item => {
      const existingName = (item?.itemName || "").toLowerCase().trim();
      return existingName === target && item?.key !== excludeKey;
    });
  };

  // Fetch inventory data from Firebase on component mount
  useEffect(() => {
    const inventoryRef = collection(db, "inventory");
    const unsubscribe = onSnapshot(inventoryRef, (snapshot) => {
      const formatted = [];
      snapshot.forEach((doc) => {
        formatted.push({
          key: doc.id,
          ...doc.data(),
        });
      });
      // Sort by creation date (newest first) and then by update date
      formatted.sort((a, b) => {
        const aDate = a.updatedAt || a.createdAt || '';
        const bDate = b.updatedAt || b.createdAt || '';
        
        // If both have dates, compare them
        if (aDate && bDate) {
          return bDate.localeCompare(aDate); // Descending order (newest first)
        }
        
        // If only one has a date, prioritize the one with date
        if (aDate && !bDate) return -1; // a comes first
        if (!aDate && bDate) return 1;  // b comes first
        
        // If neither has a date, maintain original order (keep existing items at bottom)
        return 0;
      });
      setInventoryList(formatted);
    }, (error) => {
      // Silently handle permission errors during sign-out
      if (error.code === 'permission-denied') {
        console.log('Inventory listener stopped due to sign-out');
        return;
      }
      console.error('Error fetching inventory:', error);
    });
    
    // Store unsubscribe function for manual cleanup
    setUnsubscribeFunctions(prev => [...prev, unsubscribe]);
    
    return unsubscribe;
  }, [db]);

   // Fetch user details for sidebar profile (search all personnel types)
  useEffect(() => {
  const personnelTypes = ["DentistOwner", "AssociateDentist", "ClinicStaff"];
  const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      for (const {} of personnelTypes) {
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          setUserDetails(snapshot.data());
          break;
        }
      }
    }
  });
  
  // Store unsubscribe function for manual cleanup
  setUnsubscribeFunctions(prev => [...prev, authUnsubscribe]);
  
  return () => {
    authUnsubscribe();
  };
}, [auth, db]);

  // Validation function for individual fields
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case "itemName":
        if (!value || !value.trim()) {
          return "Item name is required";
        }
        return "";
      case "pricePerUnit":
        if (!value || value.trim() === "") {
          return "Price per unit is required";
        } else if (parseFloat(value) < 0) {
          return "Price per unit cannot be negative";
        }
        return "";
      case "quantity":
        if (!value || value.trim() === "") {
          return "Quantity is required";
        } else if (parseInt(value) < 1) {
          return "Quantity must be at least 1";
        }
        return "";
      default:
        return "";
    }
  };

  // Handle blur event to validate individual fields
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    
    // Get the current value for the field
    let value = "";
    switch (fieldName) {
      case "itemName":
        value = itemName;
        break;
      case "pricePerUnit":
        value = pricePerUnit;
        break;
      case "quantity":
        value = quantity;
        break;
    }
    
    // Validate and set error
    const error = validateField(fieldName, value);
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  // Handle form submission for adding or editing items
  const handleSubmit = async () => {
    // Clear any previous error messages
    setErrorMessage("");
    
    // Mark all fields as touched when submit is clicked
    setTouchedFields({
      itemName: true,
      pricePerUnit: true,
      quantity: true,
    });

    // Validate required fields and set field-specific errors
    let hasErrors = false;
    const newFieldErrors = {
      itemName: validateField("itemName", itemName),
      pricePerUnit: validateField("pricePerUnit", pricePerUnit),
      quantity: validateField("quantity", quantity),
    };

    // Check if any errors exist
    if (newFieldErrors.itemName) hasErrors = true;
    if (newFieldErrors.pricePerUnit) hasErrors = true;
    if (newFieldErrors.quantity) hasErrors = true;

    // Check for duplicate item names (case-insensitive)
    if (!hasErrors && isItemNameExists(itemName, editingKey)) {
      newFieldErrors.itemName = `${itemName.trim()} is already in the list.`;
      hasErrors = true;
    }

    setFieldErrors(newFieldErrors);

    if (hasErrors) {
      return;
    }

    const docId = editingKey ? editingKey : String(itemNo);
    const normalizedName = itemName.trim().toLowerCase();
    const nowIso = new Date().toISOString();

    try {
      const itemData = {
        itemName: itemName.trim(),
        pricePerUnit: parseFloat(pricePerUnit),
        quantity: parseInt(quantity),
        totalCost,
        itemNo: itemNo !== null ? Number(itemNo) : undefined,
      };

      // Add timestamp fields
      if (!editingKey) {
        itemData.createdAt = nowIso; // new item
      } else {
        itemData.updatedAt = nowIso; // update
      }

      const inventoryRef = doc(db, "inventory", docId);

      // Use a transaction to ensure atomic write and prevent race conditions
      await runTransaction(db, async (transaction) => {
        // If creating, ensure the itemNo doc id doesn't already exist
        if (!editingKey) {
          const existing = await transaction.get(inventoryRef);
          if (existing.exists()) {
            throw new Error("DUPLICATE_ITEMNO");
          }
          transaction.set(inventoryRef, itemData);
        } else {
          transaction.set(inventoryRef, itemData, { merge: true });
        }
      });

      // Close dialog and reset
      setIsModalOpen(false);
      resetForm();

      // Show success snackbar
      const message = editingKey 
        ? `"${itemName.trim()}" has been updated!`
        : `"${itemName.trim()}" has been added successfully!`;
      setAddUpdateSnackbarMessage(message);
      setShowAddUpdateSnackbar(true);
    } catch (error) {
      if (error?.message === "DUPLICATE_NAME") {
        setFieldErrors((prev) => ({ ...prev, itemName: `${itemName.trim()} is already in the list.` }));
        setTouchedFields((prev) => ({ ...prev, itemName: true }));
        setShowAddUpdateSnackbar(false);
        setAddUpdateSnackbarMessage("");
        return;
      }

      if (error?.message === "DUPLICATE_ITEMNO") {
        setErrorMessage(`An item with Item No ${docId} already exists.`);
        return;
      }

      console.error("Error saving inventory item:", error);
      setErrorMessage(error?.message || "Failed to save item. Check console for details.");
    }
  };

  // Handle edit button click
  const handleEdit = (item) => {
    setEditingKey(item.key);
    setItemName(item.itemName);
    setPricePerUnit(item.pricePerUnit.toString());
    setQuantity(item.quantity.toString());
    setItemNo(item.itemNo !== undefined ? item.itemNo : null);
    setErrorMessage("");
    setIsModalOpen(true);
  };

  // Handle delete button click
  const handleDelete = (key) => {
    const item = inventoryList.find(item => item.key === key);
    setItemToDelete(item);
    setShowDeleteConfirm(true);
    handleMenuClose();
  };

  // Handle actual deletion
  const confirmDelete = () => {
    if (itemToDelete && !isDeletingInventoryItem) {
      setIsDeletingInventoryItem(true);
      const itemNameToDelete = itemToDelete.itemName;
      deleteDoc(doc(db, "inventory", itemToDelete.key)).finally(() => {
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        setIsDeletingInventoryItem(false);
        // Show success snackbar
        setDeleteSnackbarMessage(`"${itemNameToDelete}" has been deleted successfully!`);
        setShowDeleteSnackbar(true);
      });
    }
  };

  // Handle cancel deletion
  const cancelDelete = () => {
    if (isDeletingInventoryItem || isCancellingInventoryDelete) return;
    setIsCancellingInventoryDelete(true);
    setTimeout(() => {
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setIsCancellingInventoryDelete(false);
    }, 300);
  };

  // Reset form fields
  const resetForm = () => {
    setItemName("");
    setPricePerUnit("");
    setQuantity("");
    setItemNo(null);
    setEditingKey(null);
    setErrorMessage("");
    // Reset touch-based validation
    setTouchedFields({
      itemName: false,
      pricePerUnit: false,
      quantity: false,
    });
    // Reset field errors
    setFieldErrors({
      itemName: "",
      pricePerUnit: "",
      quantity: "",
    });
    setFieldErrors({
      itemName: "",
      pricePerUnit: "",
      quantity: "",
    });
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Handle logout

  const handleMenuClose = () => setOpenMenuKey(null);
  
  // sorting helper
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  // derive sorted inventory (memoized)
  const sortedInventory = React.useMemo(() => {
    const arr = [...inventoryList];
    arr.sort((a, b) => {
      const getVal = (item) => {
        if (sortField === "itemNo") return Number(item.itemNo ?? 0);
        if (sortField === "itemName") return (item.itemName || "").toLowerCase();
        if (sortField === "pricePerUnit") return Number(item.pricePerUnit || 0);
        if (sortField === "quantity") return Number(item.quantity || 0);
        if (sortField === "totalCost") return Number(item.totalCost || 0);
        return item[sortField] ?? "";
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortOrder === "asc" ? -1 : 1;
      if (va > vb) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [inventoryList, sortField, sortOrder]);

  // Pagination logic (use sortedInventory)
  const totalPages = Math.ceil(sortedInventory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedInventory.slice(startIndex, endIndex);
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    handleMenuClose(); // Close any open menus when changing pages
  };
  
  // Reset to first page when inventory list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [inventoryList.length]);
  useEffect(() => {
    if (isModalOpen && !editingKey) {
      // Find the lowest positive integer (starting at 1) not currently used as an itemNo.
      // This fills any gaps (e.g. if 1,2,4 exist, new itemNo => 3).
      const used = new Set(
        inventoryList
          .map(i => i.itemNo)
          .filter(n => n !== undefined && n !== null && !Number.isNaN(Number(n)))
          .map(Number)
      );
      let candidate = 1;
      while (used.has(candidate)) candidate += 1;
      setItemNo(candidate);
    }
  }, [isModalOpen, editingKey, inventoryList]);


  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  }

  return (
    <div>
      {/* All Content */}
      <Box 
        sx={boxStylesContent}>

        {/* Sidebar */}
        <Box
          display={{
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          }}>
        <Sidebar
          fName={userDetails?.firstName}
          lName={userDetails?.lastName}
          email={userDetails?.email}
        />
        </Box>

        <Box
          sx={{
            display: {
              xs: "block",
              sm: "block",
              md: "none",
              lg: "none",
              xl: "none",
            }
          }}
        >
        <Fab
          label="Add Item"
          place="top-end"
          destination="none"
          src={require("../../assets/icons/add.png")}
          open={false}
          onClick={setIsModalOpen}/>
        </Box>

          {/*Grids */}
          <Grid
            container
            // rowSpacing={2}
            // columnSpacing={2}
            sx={{
              width: "100%",
              height: "100vh",
              // backgroundColor: "blue",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
                // backgroundColor: "blue",
              },
            }}>


            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
            >

              {/* Main Content */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#FDF9F3",
                  // backgroundColor: "#f8f5ef",
                }}>

                <Stack
                  direction="column">

      
                 <Header
                    count={2}
                    page="Inventory"
                    setIsModalOpen={setIsModalOpen}
                  />

                  {/* Main Content Area */}
                  <Box 
                    sx={{
                      flex: 1,
                      background: {
                        xs : "#FEFEFE",
                        sm : "#FEFEFE",
                        md : "#FDF9F3",
                        lg : "#FDF9F3",
                        xl : "#FDF9F3",
                      },
                      // padding: "0px 0 20px 0"
                    }}>

                      <Box
                        sx={{
                          maxWidth: "100%",
                          margin: "0 auto",
                          padding: {
                              xs : "0",
                              sm : "0",
                              md : "0 20px",
                              lg : "0 20px",
                              xl : "0 20px",
                            }
                        }}>

                          {/* Table Container */}
                      <TableContainer
                        component={Paper}
                          sx={{
                            display: {
                              xs : "none",
                              sm : "none",
                              md : "block",
                              lg : "block",
                              cl : "block",
                            },
                            // borderRadius: "10px",
                            boxShadow: "0px 2px 8px .5px #59544A",
                            maxHeight: "650px",
                            minHeight:"650px",
                            // padding: "20px",
                            marginTop: "20px",
                            borderRadius: "20px",
                            // backgroundColor: "#C5A572",
                        }}>

                        <Table
                          aria-label="simple table"
                          stickyHeader
                          stickyFooter
                          sx={{
                            padding: "0 20px 0 20px",
                            tableLayout: "fixed",
                            width: "100%",
                          }}
                        >

                          {/* Table Head */}
                          <TableHead>
                            <TableRow
                            sx={{
                              // backgroundColor: "mediumaquamarine"
                              // padding: "20px"
                            }}>
                              <TableCell
                                onClick={() => handleSort("itemNo")}
                                sx={{
                                 borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Item No
                                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                                  {sortField === "itemNo" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                                </Box>
                              </TableCell>

                              <TableCell
                                onClick={() => handleSort("itemName")}
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Item Name
                                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                                  {sortField === "itemName" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                                </Box>
                              </TableCell>


                              <TableCell
                                onClick={() => handleSort("pricePerUnit")}
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Price per Unit
                                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                                  {sortField === "pricePerUnit" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                                </Box>
                              </TableCell>

                              <TableCell
                                onClick={() => handleSort("quantity")}
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Quantity
                                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                                  {sortField === "quantity" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                                </Box>
                              </TableCell>


                              <TableCell
                                onClick={() => handleSort("totalCost")}
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Cost
                                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                                  {sortField === "totalCost" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                                </Box>
                              </TableCell>


                              <TableCell
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600"
                                }}>
                              </TableCell>
                            </TableRow>
                          </TableHead>

                           <TableBody>
                            {currentItems.map((item) => (
                              <TableRow
                                key={item.key}
                                sx={{
                                  ":hover": {
                                    backgroundColor: "rgba(46, 45, 45, 0.1)"
                                  }
                                }}>
                                <TableCell
                                  sx={{
                                    borderBottom: "none",
                                    padding: '20px 10px 10px 10px',
                                    width: 80,
                                    fontWeight: 600
                                  }}>
                                  {item.itemNo !== undefined && item.itemNo !== null
                                    ? String(Number(item.itemNo)).padStart(4, "0")
                                    : "-"}
                                </TableCell>

                                <TableCell
                                  sx={{
                                    borderBottom: "none",
                                    padding: '20px 10px 10px 10px',
                                  }}>{item.itemName}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    borderBottom: "none",
                                    padding: '20px 10px 10px 10px',
                                  }}>₱ {Number(item.pricePerUnit).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    borderBottom: "none",
                                    padding: '20px 10px 10px 10px',
                                  }}>{item.quantity}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    borderBottom: "none",
                                    padding: '20px 10px 10px 10px',
                                  }}>₱ {Number(item.totalCost).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    borderBottom: "none",
                                    padding: '20px 10px 10px 10px',
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                    alignItems: "center"
                                  }}>
                                      <Tooltip
                                              title="Edit"
                                              placement="top"
                                              arrow
                                            >
                                      <Box
                                        onClick={() => handleEdit(item)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "40px",
                                          width: "40px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../assets/icons/edit_v2.png")}
                                          size="small"/>
                                      </Box>
                                      </Tooltip>

                                        <Tooltip
                                              title="Delete"
                                              placement="top"
                                              arrow
                                            >
                                      <Box
                                        onClick={() => handleDelete(item.key)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "40px",
                                          width: "40px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../assets/delete.png")}
                                          size="small"/>
                                      </Box>
                                      </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>

                          {/* Table Footer */}
                          <TableFooter>
                            <TableRow
                            sx={{
                              position: "sticky",  
                              bottom: 0,            
                              backgroundColor: "#FEFEFE",
                              zIndex: 2,            
                            }}>
                              <TableCell
                                colSpan={6}
                                align="center"
                                sx={{
                                  borderBottom: "none",
                                  borderTop : "1px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px",
                                  fontWeight: "600",
                                  fontSize: "14px"
                                }}>Grand Total: ₱ {inventoryList
                                    .reduce((acc, item) => acc + (item.totalCost || 0), 0)
                                    .toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                          
                        </Table>  

                      </TableContainer>

                      <Box
                        sx={{
                          display: {
                              xs : "block",
                              sm : "block",
                              md : "none",
                              lg : "none",
                              xl : "none",
                            },
                        }}>

          <Grid
            container
            columnSpacing={2}
            sx={{
              backgroundColor: "#2E2D2D",
              width: "100%",
              padding: "10px",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
              },
            }}>

            {/* Number */}
            <Grid 
              size={{ 
                xs: 2.4, 
                sm: 2.4, 
                md: 2.4, 
                lg: 2.4, 
                xl: 2.4 
              }}
              sx={{
                justifyItems: "center",
                padding: "5px"
              }} 
              item
            >
                        <Stack
                          direction="row"
                          onClick={() => handleSort("itemNo")}
                          sx={{
                            cursor: "pointer"
                          }}>
                          <Text
                            text={sortField === "itemNo" ? "No." : "No."}
                            variant="serviceTableLable"/>
                          <Text
                            text={sortField === "itemNo" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                            variant="serviceTableLable"/>
                        </Stack>
            </Grid>

            {/* Name */}
            <Grid 
              size={{ 
                xs: 2.4, 
                sm: 2.4, 
                md: 2.4, 
                lg: 2.4, 
                xl: 2.4 
              }}
              sx={{
                justifyItems: "center",
                padding: "5px"
              }} 
              item
            >
                        <Stack
                          direction="row"
                          onClick={() => handleSort("itemName")}
                          sx={{
                            cursor: "pointer"
                          }}>
                          <Text
                            text={sortField === "itemName" ? "Name" : "Name"}
                            variant="serviceTableLable"/>
                          <Text
                            text={sortField === "itemName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                            variant="serviceTableLable"/>
                        </Stack>
            </Grid>

            {/* Price */}
            <Grid 
              size={{ 
                xs: 2.4, 
                sm: 2.4, 
                md: 2.4, 
                lg: 2.4, 
                xl: 2.4 
              }}
              sx={{
                justifyItems: "center",
                padding: "5px"
              }} 
              item
            >
                        <Stack
                          direction="row"
                          onClick={() => handleSort("pricePerUnit")}
                          sx={{
                            cursor: "pointer"
                          }}>
                          <Text
                            text={sortField === "pricePerUnit" ? "Price" : "Price"}
                            variant="serviceTableLable"/>
                          <Text
                            text={sortField === "pricePerUnit" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                            variant="serviceTableLable"/>
                        </Stack>
            </Grid>

            {/* Quantity */}
            <Grid 
              size={{ 
                xs: 2.4, 
                sm: 2.4, 
                md: 2.4, 
                lg: 2.4, 
                xl: 2.4 
              }}
              sx={{
                justifyItems: "center",
                padding: "5px"
              }} 
              item
            >
                        <Stack
                          direction="row"
                          onClick={() => handleSort("quantity")}
                          sx={{
                            cursor: "pointer"
                          }}>
                          <Text
                            text={sortField === "quantity" ? "Quantity" : "Quantity"}
                            variant="serviceTableLable"/>
                          <Text
                            text={sortField === "quantity" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                            variant="serviceTableLable"/>
                        </Stack>
            </Grid>

            {/* Cost */}
            <Grid 
              size={{ 
                xs: 2.4, 
                sm: 2.4, 
                md: 2.4, 
                lg: 2.4, 
                xl: 2.4 
              }}
              sx={{
                justifyItems: "center",
                padding: "5px"
              }} 
              item
            >
                        <Stack
                          direction="row"
                          onClick={() => handleSort("totalCost")}
                          sx={{
                            cursor: "pointer"
                          }}>
                          <Text
                            text={sortField === "totalCost" ? "Total" : "Total"}
                            variant="serviceTableLable"/>
                          <Text
                            text={sortField === "totalCost" ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                            variant="serviceTableLable"/>
                        </Stack>
            </Grid>

            

</Grid>

<Divider/>
                      </Box>

                      <Box
                        sx={{
                            display: {
                              xs : "block",
                              sm : "block",
                              md : "none",
                              lg : "none",
                              xl : "none",
                            },
                            maxHeight: "650px",
                            minHeight:"650px",
                        }}>

                          {currentItems.map((item) => (
                              <>
                                <Grid
                                  key={item.key}
                                      container
                                      columnSpacing={2}
                                      sx={{
                                        padding: "10px 15px",
                                        width: "100%",
                                        alignItems: "center",
                                        "& .MuiGrid-item": {
                                          display: "flex",
                                          justifyContent: "center",
                                        },
                                      }}>

                                      {/* Main Content */}
                                      <Grid
                                        size={{ 
                                          xs: 12, 
                                          sm: 12, 
                                          md: 12, 
                                          lg: 5, 
                                          xl: 4 
                                        }} 
                                        item
                                        sx={{
                                        }} 
                                      >
                                        <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                          alignItems: "center"
                                        }}>

                                        <Box
                                          sx={{
                                            height: "40px",
                                            borderRadius: "10px",
                                            boxShadow: "0px 1px 5px rgba(0,0,0,0.25)", 
                                            alignContent: "center",
                                          }}>

                                        <Typography
                                          variant="historyDateTime"
                                          sx={{      
                                            fontWeight: {
                                              xs: "600", 
                                              sm: "600", 
                                              md: "600", 
                                              lg: "100", 
                                              xl: "100" 
                                            },
                                          }}>
                                            {item.itemNo !== undefined && item.itemNo !== null
                                    ? String(Number(item.itemNo)).padStart(4, "0")
                                    : "-"}
                                        </Typography>
                                        </Box>

                                        <Stack
                                          direction="row"
                                          sx={{
                                            width: "100%"
                                          }}>
                                      {/* Main Content */}
                                      <Grid
                                        size={{ 
                                          xs: 6, 
                                          sm: 5, 
                                          md: 4, 
                                          lg: 4, 
                                          xl: 4 
                                        }} 
                                        item
                                        sx={{
                                        }} 
                                      >
                                        <Stack
                                          direction="column"
                                          sx={{
                                          transition: " 0.3s ease" 
                                          }}>
                                            
                                        <Typography
                                          variant="historyDateTime"
                                          sx={{      
                                            fontWeight: {
                                              xs: "600", 
                                              sm: "600", 
                                              md: "600", 
                                              lg: "100", 
                                              xl: "100" 
                                            },
                                            padding: "5px 10px"
                                          }}>
                                            {item.itemName}
                                        </Typography>

                                        <Typography
                                          variant="historyDateTime"
                                          sx={{      
                                            color: "rgba(44, 37, 30, 0.7)",
                                            fontSize: "14px",
                                            fontWeight: "300",
                                            padding: "0 10px"
                                          }}>
                                            ₱ {Number(item.pricePerUnit).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                        </Typography>

                                        {/* <Text
                                          text=
                                          variant="serviceLabels"/> */}

                                        </Stack>
                                      </Grid>

                                      {/* Main Content */}
                                      <Grid
                                        size={{ 
                                          xs: 6, 
                                          sm: 7, 
                                          md: 8, 
                                          lg: 8, 
                                          xl: 8 
                                        }} 
                                        item
                                        sx={{
                                        }} 
                                      >
                                        <Stack
                                          direction="column"
                                          sx={{
                                          }}>
                                            
                                        <Typography
                                          variant="historyDateTime"
                                          sx={{      
                                            fontWeight: {
                                              xs: "600", 
                                              sm: "600", 
                                              md: "600", 
                                              lg: "100", 
                                              xl: "100" 
                                            },
                                            padding: "5px 10px"
                                          }}>
                                            {item.quantity}
                                        </Typography>

                                        <Text
                                          text={Number(item.pricePerUnit).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                          variant="serviceLabels"/>

                                        </Stack>
                                      </Grid>

                                      <Stack
                                        direction="row"
                                        spacing={1}
                                      >

                                          <Tooltip
                                              title="Edit"
                                              placement="top"
                                              arrow
                                            >
                                      <Box
                                        onClick={() => handleEdit(item)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "40px",
                                          width: "40px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../assets/icons/edit_v2.png")}
                                          size="small"/>
                                      </Box>
                                      </Tooltip>

                                        <Tooltip
                                              title="Delete"
                                              placement="top"
                                              arrow
                                            >
                                      <Box
                                        onClick={() => handleDelete(item.key)}
                                        sx={{
                                          borderRadius: "50px",
                                          height: "40px",
                                          width: "40px",
                                          cursor: "pointer",
                                          ":hover": {
                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                          }
                                        }}>
                                        <Icon
                                          src={require("../../assets/delete.png")}
                                          size="small"/>
                                      </Box>
                                      </Tooltip>
                                      </Stack>
                                      
                                      </Stack>

                                        </Stack>
                                      </Grid>




                                    </Grid>
                                
                                <Divider/>
                                
                              </>
                            ))}
                        
                      </Box>
 <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                marginTop: "20px",
                padding: "20px 0"
              }}>

                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => handlePageChange(page)}
                  shape="rounded"
                  variant="outlined"
                  siblingCount={1}
                  boundaryCount={2}
                  color="primary"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 600,
                      fontSize: "16px",
                      borderRadius: "6px",
                      minWidth: "40px",
                      border: "1px solid #C5A572",
                      backgroundColor: "#FEFEFE",
                                  minHeight: "40px",  
                    "&.Mui-selected": {
                      backgroundColor: "#2E2D2D"
                    },
                    },
                  }}
                />
</div>

                     {isModalOpen && (
  <Dialog
    open={isModalOpen}
    onClose={handleModalClose}
    maxWidth="sm"
    fullWidth={false}
    sx={{
      "& .MuiDialog-paper": {
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "#FDF9F3",
        boxShadow: "0 18px 60px rgba(44,37,30,0.14)",
        width: "500px",
        maxWidth: "90vw",
      }
    }}
    PaperProps={{
      sx: {
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "#FDF9F3",
        boxShadow: "0 18px 60px rgba(44,37,30,0.14)",
        m: 0,
      },
    }}
    aria-labelledby="inventory-dialog-title"
  >
    <DialogTitle
      id="inventory-dialog-title"
      sx={{
        bgcolor: "#FDF9F3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        px: 3,
        py: 2,
        borderRadius: "16px 16px 0 0",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
        {editingKey ? "Edit Item" : "Add Item"}
      </Typography>

      <IconButton
        onClick={handleModalClose}
        size="small"
        sx={{
          position: "absolute",
          right: 16,
          width: 28,
          height: 28,
          border: "1px solid rgba(44,37,30,0.06)",
          bgcolor: "#fff",
          color: "#2E2D2D",
          fontSize: "16px",
          "&:hover": { bgcolor: "#C5A572", color: "#FEFEFE" },
        }}
        aria-label="close"
      >
        ✕
      </IconButton>
    </DialogTitle>

    <DialogContent
      sx={{
        pt: 2,
        px: 3,
        pb: 3,
        bgcolor: "#FEFEFE",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        gap: 0,
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <Box
        component="form"
        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
        sx={{
          width: "100%",
        }}
      >
        <Grid container spacing={2} direction="column">
          <Grid item xs={12} sx={{ width: "100%" }}>
            <Box sx={{ width: "100%", "& > div": { width: "100%" }, "& .MuiTextField-root": { width: "100%" }, "& .MuiInputBase-root": { width: "100%" } }}>
              <TextFieldComp
                label="Item No"
                value={itemNo !== null ? String(itemNo).padStart(4, "0") : ""}
                readOnly
                state={true}
                onChange={() => {}} // readOnly - no-op
                nonClickable={true}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ width: "100%" }}>
            <Box sx={{ width: "100%", "& > div": { width: "100%" }, "& .MuiTextField-root": { width: "100%" }, "& .MuiInputBase-root": { width: "100%" } }}>

                            <TextFieldComp
                              placeholder="Enter item name"
                              id="itemName"
                              label="Item Name"
                              type="text"
                              value={itemName}
                              color="primary"
                              onChange={(e) => {
                  const newValue = e.target.value;
                  setItemName(newValue);
                  // Clear error when user starts typing
                  if (touchedFields.itemName) {
                    if (newValue.trim()) {
                      setFieldErrors(prev => ({ ...prev, itemName: "" }));
                    } else {
                      // Re-validate if field becomes empty
                      const error = validateField("itemName", newValue);
                      setFieldErrors(prev => ({ ...prev, itemName: error }));
                    }
                  }
                }}
                              onBlur={() => handleFieldBlur("itemName")}
                              state={false}
                              requires={true}
                              error={touchedFields.itemName && Boolean(fieldErrors.itemName)}
                helperText={touchedFields.itemName ? (fieldErrors.itemName || "") : ""}
                />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ width: "100%" }}>
            <Box sx={{ width: "100%", "& > div": { width: "100%" }, "& .MuiTextField-root": { width: "100%" }, "& .MuiInputBase-root": { width: "100%" } }}>
                            <TextFieldComp
                              placeholder="Enter price"
                              id="message"
                              label="Price per Unit"
                              type="number"
                min="0"
                step="0.01"
                              value={pricePerUnit}
                              color="primary"
                              onKeyDown={(e) => {
                  // Prevent minus key, 'e', 'E', '+' from being pressed
                  if (e.key === '-' || e.key === 'Minus' || 
                      e.key === 'e' || e.key === 'E' || 
                      e.key === '+' || e.key === 'Plus') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  let newValue = e.target.value;
                  // Remove any minus signs, 'e', 'E', '+' that might have been pasted or entered
                  if (newValue.includes('-')) {
                    newValue = newValue.replace(/-/g, '');
                  }
                  if (newValue.includes('e') || newValue.includes('E')) {
                    newValue = newValue.replace(/[eE]/g, '');
                  }
                  if (newValue.includes('+')) {
                    newValue = newValue.replace(/\+/g, '');
                  }
                  // Block negative values
                  if (newValue !== "") {
                    const numValue = parseFloat(newValue);
                    if (!isNaN(numValue) && numValue < 0) {
                      // If negative value somehow gets through, don't update
                      return;
                    }
                  }
                  setPricePerUnit(newValue);
                  // Validate in real-time
                  if (touchedFields.pricePerUnit) {
                    const error = validateField("pricePerUnit", newValue);
                    setFieldErrors(prev => ({ ...prev, pricePerUnit: error }));
                  }
                }}
                              onBlur={() => handleFieldBlur("pricePerUnit")}
                              state={false}
                              requires={true}
                              error={touchedFields.pricePerUnit && Boolean(fieldErrors.pricePerUnit)}
                helperText={touchedFields.pricePerUnit ? (fieldErrors.pricePerUnit || "") : ""}
                />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ width: "100%" }}>
            <Box sx={{ width: "100%", "& > div": { width: "100%" }, "& .MuiTextField-root": { width: "100%" }, "& .MuiInputBase-root": { width: "100%" } }}>
                            <TextFieldComp
                              placeholder="Enter quantity"
                              id="message"
                              label="Quantity"
                              type="number"
                min="1"
                step="1"
                              value={quantity}
                              color="primary"
                              onKeyDown={(e) => {
                  // Prevent minus key, 'e', 'E', '+', and decimal point from being pressed
                  // Quantity should be whole numbers only
                  if (e.key === '-' || e.key === 'Minus' || 
                      e.key === 'e' || e.key === 'E' || 
                      e.key === '+' || e.key === 'Plus' ||
                      e.key === '.') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  let newValue = e.target.value;
                  // Remove any minus signs, 'e', 'E', '+', and decimal points that might have been pasted or entered
                  if (newValue.includes('-')) {
                    newValue = newValue.replace(/-/g, '');
                  }
                  if (newValue.includes('e') || newValue.includes('E')) {
                    newValue = newValue.replace(/[eE]/g, '');
                  }
                  if (newValue.includes('+')) {
                    newValue = newValue.replace(/\+/g, '');
                  }
                  // Remove decimal points for quantity (whole numbers only)
                  if (newValue.includes('.')) {
                    newValue = newValue.replace(/\./g, '');
                  }
                  // Block negative values and values less than 1
                  if (newValue !== "") {
                    const numValue = parseFloat(newValue);
                    if (!isNaN(numValue) && (numValue < 1 || numValue < 0)) {
                      // If value less than 1 or negative is entered, don't update
                      return;
                    }
                  }
                  setQuantity(newValue);
                  // Validate in real-time
                  if (touchedFields.quantity) {
                    const error = validateField("quantity", newValue);
                    setFieldErrors(prev => ({ ...prev, quantity: error }));
                  }
                }}
                              onBlur={() => handleFieldBlur("quantity")}
                              state={false}
                              requires={true}
                              error={touchedFields.quantity && Boolean(fieldErrors.quantity)}
                helperText={touchedFields.quantity ? (fieldErrors.quantity || "") : ""}
                />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ width: "100%" }}>
            <Box sx={{ mt: 0.5, pt: 2, pb: 2, pr: 2, borderRadius: 1, bgcolor: "#FBFAF7", display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box", pl: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: "left" }}>Total Cost</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2E2D2D" }}>
                ₱ {Number(totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 3, pt: 0, bgcolor: "#FEFEFE", justifyContent: "center", borderRadius: "0 0 16px 16px" }}>
      <Button
        onClick={handleSubmit}
        variant="contained"
        sx={{
          backgroundColor: "#C5A572",
          color: "#FEFEFE",
          textTransform: "none",
          width: "100%",
          py: 1,
          fontSize: "16px",
          fontWeight: "600",
          "&:hover": { backgroundColor: "#A68B6E" }
        }}
      >
        {editingKey ? "Update" : "Submit"}
      </Button>
    </DialogActions>
  </Dialog>
)}
                       

                        {/* Delete Confirmation Modal */}
                        <Dialog
                          open={showDeleteConfirm}
                          onClose={(_, reason) => {
                            // Allow closing on backdrop click
                            if (reason === "backdropClick") {
                              if (!isDeletingInventoryItem && !isCancellingInventoryDelete) {
                                cancelDelete();
                              }
                              return;
                            }
                            if (!isDeletingInventoryItem && !isCancellingInventoryDelete) {
                              cancelDelete();
                            }
                          }}
                          maxWidth="xs"
                          fullWidth
                          sx={{
                            zIndex: 2000,
                            "& .MuiDialog-paper": { borderRadius: 3, backgroundColor: "#FFFFFF", paddingBottom: 2 }
                          }}
                          BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.6)" } }}
                        >
                          <DialogTitle
                            sx={{
                              textAlign: "center",
                              fontWeight: 700,
                              color: "#C94D34",
                              fontSize: "22px",
                              padding: "28px 32px 12px 32px",
                              position: "relative",
                            }}
                          >
                            Confirm Inventory Item Deletion
                            <IconButton
                              onClick={cancelDelete}
                              disabled={isDeletingInventoryItem || isCancellingInventoryDelete}
                              sx={{
                                position: "absolute",
                                right: 16,
                                top: 16,
                                color: "#6D6257",
                                "&:hover": { backgroundColor: "rgba(109, 98, 87, 0.08)" },
                              }}
                            >
                              {isCancellingInventoryDelete ? (
                                <CircularProgress size={16} sx={{ color: "#6D6257" }} />
                              ) : (
                                <Close fontSize="small" />
                              )}
                            </IconButton>
                          </DialogTitle>
                          <DialogContent sx={{ padding: "0 32px 8px 32px" }}>
                            <Stack spacing={2} alignItems="center">
                              <Typography sx={{ color: "#2C251E", fontSize: "16px", fontWeight: 500, textAlign: "center" }}>
                                Are you sure you want to delete <strong>"{itemToDelete?.itemName}"</strong>?
                              </Typography>
                              <Typography sx={{ color: "rgba(44, 37, 30, 0.6)", fontSize: "14px", textAlign: "center" }}>
                                This action cannot be undone.
                              </Typography>
                            </Stack>
                          </DialogContent>
                          <DialogActions
                            sx={{ padding: "12px 32px 28px 32px", justifyContent: "center", gap: 2 }}
                          >
                            <Button
                              onClick={cancelDelete}
                              variant="outlined"
                              sx={{
                                color: "#6D6257",
                                borderColor: "#6D6257",
                                padding: "10px 22px",
                                borderRadius: 20,
                                fontSize: "15px",
                                fontWeight: 600,
                                minWidth: "120px",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "rgba(109, 98, 87, 0.08)", borderColor: "#6D6257" },
                              }}
                              disabled={isDeletingInventoryItem || isCancellingInventoryDelete}
                            >
                              {isCancellingInventoryDelete ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <CircularProgress size={16} sx={{ color: "#6D6257" }} />
                                  <span>Cancelling...</span>
                                </Box>
                              ) : (
                                "No, Cancel"
                              )}
                            </Button>
                            <Button
                              onClick={confirmDelete}
                              variant="contained"
                              sx={{
                                backgroundColor: "#C94D34",
                                color: "#fff",
                                padding: "10px 22px",
                                borderRadius: 20,
                                fontSize: "15px",
                                fontWeight: 600,
                                minWidth: "120px",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": { backgroundColor: "#A93F28", boxShadow: "none" },
                              }}
                              disabled={isDeletingInventoryItem || isCancellingInventoryDelete}
                            >
                              {isDeletingInventoryItem ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                                  <span>Deleting...</span>
                                </Box>
                              ) : (
                                "Yes, Delete"
                              )}
                            </Button>
                          </DialogActions>
                        </Dialog>

                      </Box>

                    </Box>
                  
                </Stack>

              </Box>

            </Grid>

          </Grid>  

      </Box>

    


    <SplashScreen
      isOpen={showCustomAlert}
      image={alertImage}
      button={alertButton}
      buttonType={alertButtonType}
      destination={alertLink}
      onClose={() => setShowCustomAlert(false)}/>

      {/* Add/Update Success Snackbar */}
      <Snackbar
        open={showAddUpdateSnackbar}
        autoHideDuration={5000}
        onClose={() => {
          setShowAddUpdateSnackbar(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => {
            setShowAddUpdateSnackbar(false);
          }}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {addUpdateSnackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Delete Success Snackbar */}
      <Snackbar
        open={showDeleteSnackbar}
        autoHideDuration={5000}
        onClose={() => {
          setShowDeleteSnackbar(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => {
            setShowDeleteSnackbar(false);
          }}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {deleteSnackbarMessage}
        </MuiAlert>
      </Snackbar>

    </div>
  );
};

export default ManageInventory;